/**
 * Pure(ish) canvas helpers for the Smart Create AI flow. Extracted from
 * EditorPage so they can be unit-tested in isolation and reused by any
 * future AI tool that needs the same inpaint plumbing.
 *
 * All helpers are synchronous canvas-in / canvas-out except
 * `loadImageToCanvas` which awaits the underlying `<img>` load.
 */

/** Smart Create Insert max edge length sent to the model. OpenAI's
 *  edit endpoint outputs at most 1536² and BFL FLUX's largest accepted
 *  size is also 1536 on the long axis, so anything larger is wasted
 *  bandwidth before the model even runs. The wrapper's compositor
 *  resizes the result back to the layer's native resolution before
 *  destination-in clipping, so downscaling on send costs no fidelity
 *  inside the marquee region. */
export const SMART_CREATE_MAX_INPUT_EDGE_PX = 1536;

/** Smart Create Blend feather sizing. The mask boundary fades over a
 *  band proportional to the marquee's smaller side, clamped so very
 *  small marquees still get a perceptible transition and very large
 *  marquees don't dissolve too far inward. Picked empirically — 5%
 *  matches a typical "object-sized" marquee comfortably. */
export const FEATHER_RADIUS_RATIO = 0.05;
export const FEATHER_RADIUS_MIN_PX = 8;
export const FEATHER_RADIUS_MAX_PX = 32;

/** Compute the feather radius (in pixels) for a Blend-mode mask given
 *  the marquee's bbox. Pure math — `featherMask` consumes the result. */
export function computeFeatherRadius(bboxWidth: number, bboxHeight: number): number {
  return Math.max(
    FEATHER_RADIUS_MIN_PX,
    Math.min(
      FEATHER_RADIUS_MAX_PX,
      Math.round(Math.min(bboxWidth, bboxHeight) * FEATHER_RADIUS_RATIO),
    ),
  );
}

/** Compute the destination dimensions for `fitCanvasToMaxDim` without
 *  actually performing the resize. Pure math — separable so callers
 *  that only need the dims (e.g., to budget memory) can skip the draw. */
export function computeFitDims(
  width: number,
  height: number,
  maxDim: number,
): { width: number; height: number; scale: number } {
  const longest = Math.max(width, height);
  if (longest <= maxDim) return { width, height, scale: 1 };
  const scale = maxDim / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    scale,
  };
}

/** Convert the editor's marquee mask (alpha=255 inside the selection,
 *  alpha=0 outside) to OpenAI's edit-mask convention (alpha=0 inside,
 *  alpha=255 outside). Returns a fresh canvas the same size as the
 *  source — the caller's canvas is left untouched. */
export function invertMaskAlpha(mask: HTMLCanvasElement): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = mask.width;
  out.height = mask.height;
  const ctx = out.getContext('2d');
  if (!ctx) return mask;
  // Start fully opaque white, then punch a transparent hole using the
  // mask's filled region (destination-out reads the mask's alpha and
  // subtracts it from the destination).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.globalCompositeOperation = 'destination-out';
  ctx.drawImage(mask, 0, 0);
  return out;
}

/** Downscale a canvas so its longest side is <= maxDim, preserving
 *  aspect ratio. Returns the input unchanged when it's already small
 *  enough. The Smart Create inpaint route caps payloads at ~8 MB per
 *  data URL — at full 24-bit color a 4096² PNG would exceed that on
 *  its own — and OpenAI's edit endpoint outputs at most 1536² anyway,
 *  so sending higher-resolution input is wasted bandwidth. */
export function fitCanvasToMaxDim(src: HTMLCanvasElement, maxDim: number): HTMLCanvasElement {
  const dims = computeFitDims(src.width, src.height, maxDim);
  if (dims.scale === 1) return src;
  const out = document.createElement('canvas');
  out.width = dims.width;
  out.height = dims.height;
  const ctx = out.getContext('2d');
  if (!ctx) return src;
  ctx.drawImage(src, 0, 0, out.width, out.height);
  return out;
}

/** Soften a hard binary mask into a feathered alpha gradient that
 *  fades to zero at the marquee boundary. Used by Smart Create's
 *  inpaint mode so the AI-generated content blends into the
 *  surrounding photo instead of meeting it at a hard edge.
 *
 *  Approach: blur the mask, then remap the alpha through
 *  `clamp((a - 128) * 2)` so the blurred edge's 50% point lands at
 *  alpha=0. The result has alpha=255 in the marquee's interior
 *  (eroded inward by ~radius), tapering to 0 right at the marquee's
 *  original boundary — no halo extending outward, no hard cut at the
 *  edge. */
export function featherMask(mask: HTMLCanvasElement, radius: number): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = mask.width;
  out.height = mask.height;
  const ctx = out.getContext('2d');
  if (!ctx) return mask;
  ctx.filter = `blur(${radius}px)`;
  ctx.drawImage(mask, 0, 0);
  ctx.filter = 'none';
  const img = ctx.getImageData(0, 0, out.width, out.height);
  for (let i = 3; i < img.data.length; i += 4) {
    const v = img.data[i];
    img.data[i] = Math.max(0, Math.min(255, (v - 128) * 2));
  }
  ctx.putImageData(img, 0, 0);
  return out;
}

/** Decode a data URL into an HTMLCanvasElement. Used to convert the
 *  Smart Create response back into something the canvas hook can
 *  composite. */
export function loadImageToCanvas(dataUrl: string): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const out = document.createElement('canvas');
      out.width = img.naturalWidth;
      out.height = img.naturalHeight;
      const ctx = out.getContext('2d');
      if (!ctx) {
        reject(new Error('2D context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(out);
    };
    img.onerror = () => reject(new Error('Failed to decode generated image'));
    img.src = dataUrl;
  });
}
